/**
 * =============================================================================
 * ATTACH IMAGES TRAY (ADR-022 §10(A) — PR4b backbone fold S3/A5)
 * =============================================================================
 *
 * The dismissible post-create attach stage shared by the staff-reply and RFI
 * flows: a bordered tray with a caption, a "Done" dismiss, and the
 * AdminImageUploader wired to the staff-authored anchor reply just created.
 * Previously two verbatim copies (ReplyModerationPanel / RfiManagementPanel)
 * whose chrome, bounds, and testId scheme could drift independently.
 *
 * Bounds are the platform STAFF-REPLY defaults on purpose — both trays attach
 * to a staff-authored anchor, and the client bounds are a UX gate only (a
 * tenant-tightened §10.1 cap still 400s per file — the server is the gate).
 * The question-detail uploader mount is NOT this pattern (no tray/dismiss
 * chrome, question caps) and deliberately stays inline.
 * =============================================================================
 */

import React from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { FILE_VALIDATION } from '@find-my-part/types';
import { AdminImageUploader } from './AdminImageUploader';
import type { MediaUploadOutcome } from '../../hooks/useMediaAuthoring';
import { tokens } from '../../tokens';

export interface AttachImagesTrayProps {
  /** Tray caption (e.g. "Attach images to your reply"). */
  caption: string;
  /** Dismiss the tray (the anchor id is cleared by the owning panel). */
  onDone: () => void;
  /** Upload ONE file against the anchor reply (AdminImageUploader contract). */
  onUpload: (file: File) => Promise<MediaUploadOutcome>;
  /**
   * Test ID of the tray box itself; the Done button and uploader derive
   * `-done` / `-uploader` suffixes (the pre-fold DOM contract, so existing
   * panel tests keep matching).
   */
  testId?: string;
}

/**
 * Dismissible staff image attach tray (post-create stage of the reply / RFI
 * flows).
 */
export function AttachImagesTray({
  caption,
  onDone,
  onUpload,
  testId,
}: AttachImagesTrayProps): React.ReactElement {
  return (
    <Box
      marginBottom="medium"
      padding="small"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.md }}
      data-testid={testId}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <FlexItem>
          <Text bold>
            {caption}
          </Text>
        </FlexItem>
        <FlexItem>
          <Button
            variant="subtle"
            onClick={onDone}
            data-testid={testId ? `${testId}-done` : undefined}
          >
            Done
          </Button>
        </FlexItem>
      </Flex>
      <AdminImageUploader
        maxFiles={FILE_VALIDATION.MAX_IMAGES_PER_STAFF_REPLY}
        maxSizeMb={FILE_VALIDATION.MAX_SIZE_MB}
        allowedFormats={FILE_VALIDATION.ALLOWED_MIME_TYPES}
        onUpload={onUpload}
        testId={testId ? `${testId}-uploader` : undefined}
      />
    </Box>
  );
}
